/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isNull from 'lodash.isnull';
import {
  error
} from '@ion/core/src/logger/logger';
import {
  createAccount,
  deleteAccount,
  getApplications,
  loadAccounts,
  updateAccount
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import GroupProperties from '@ion/core/src/services/GroupProperties';
import {
  Account,
  Entity,
  Links
} from '@ion/core/src/types/entities';
import {LoadAccountsParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {addEscapeCharsToFilter} from '@ion/dls/src/helpers/setEscapeCharsToFilter';
import {QueryKey} from '@ion/query/src/config/QueryKeys';
import {removeQueries} from '@ion/query/src/utils/helpers';
import {
  refreshInitialization
} from '@/store/actions/initialization';
import {
  AsyncActionCreator,
  PageInitializeAction
} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {hasCurrentAccountEditAccess} from '../../security/Features';
import {
  getAccountId,
  getAllAccountIds
} from '../helpers';
import {setCurrentAccount} from './global-group-chooser';
import {importConfirm} from './importer';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                           = 'adminGroups';

export const INITIALIZE_ADMIN_GROUPS        = 'INITIALIZE_ADMIN_GROUPS';
export const RE_INITIALIZE_ADMIN_GROUPS     = 'RE_INITIALIZE_ADMIN_GROUPS';

// grid column state
export const SET_GRID_COLUMNS               = 'SET_ADMIN_GROUPS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS            = 'UPDATE_ADMIN_GROUPS_GRID_COLUMNS';

// groups / accounts retrieval ...
export const GET_ADMIN_GROUPS_REQUEST       = 'GET_ADMIN_GROUPS_REQUEST';
export const GET_ADMIN_GROUPS_RESPONSE      = 'GET_ADMIN_GROUPS_RESPONSE';
export const GET_ADMIN_GROUPS_ERROR         = 'GET_ADMIN_GROUPS_ERROR';

// group / account add ...
export const ADD_ADMIN_GROUP                = 'ADD_ADMIN_GROUP';
export const ADD_ADMIN_GROUP_REQUEST        = 'ADD_ADMIN_GROUP_REQUEST';
export const ADD_ADMIN_GROUP_RESPONSE       = 'ADD_ADMIN_GROUP_RESPONSE';
export const ADD_ADMIN_GROUP_ERROR          = 'ADD_ADMIN_GROUP_ERROR';

// group / account edit ...
export const EDIT_ADMIN_GROUP               = 'EDIT_ADMIN_GROUP';
export const EDIT_ADMIN_GROUP_REQUEST       = 'EDIT_ADMIN_GROUP_REQUEST';
export const EDIT_ADMIN_GROUP_RESPONSE      = 'EDIT_ADMIN_GROUP_RESPONSE';
export const EDIT_ADMIN_GROUP_ERROR         = 'EDIT_ADMIN_GROUP_ERROR';

// group / account  delete ...
export const DELETE_ADMIN_GROUP             = 'DELETE_ADMIN_GROUP';
export const DELETE_ADMIN_GROUP_REQUEST     = 'DELETE_ADMIN_GROUP_REQUEST';
export const DELETE_ADMIN_GROUP_RESPONSE    = 'DELETE_ADMIN_GROUP_RESPONSE';
export const DELETE_ADMIN_GROUP_ERROR       = 'DELETE_ADMIN_GROUP_ERROR';

// view group / account audit log ...
export const AUDIT_ADMIN_GROUP              = 'AUDIT_ADMIN_GROUP';
export const AUDIT_ADMIN_GROUP_REQUEST      = 'AUDIT_ADMIN_GROUP_REQUEST';
export const AUDIT_ADMIN_GROUP_RESPONSE     = 'AUDIT_ADMIN_GROUP_RESPONSE';
export const AUDIT_ADMIN_GROUP_ERROR        = 'AUDIT_ADMIN_GROUP_ERROR';

// group bulk import
export const IMPORT_ADMIN_GROUPS          = 'IMPORT_ADMIN_GROUPS';

// Grid refresh
export const ADMIN_GROUPS_GRID_REFRESHED  = 'ADMIN_GROUPS_GRID_REFRESHED';

// Admin Groups filter model
export const SET_ADMIN_GROUPS_GRID_FILTER_MODEL = 'SET_ADMIN_GROUPS_GRID_FILTER_MODEL';

/**
 * @initialize
 */
export const initializeAdminGroups = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_GROUPS};
};

export const reInitializeAdminGroups = (): PageInitializeAction => {
  return {type: RE_INITIALIZE_ADMIN_GROUPS};
};

/**
 * @getAdminGroups
 */
export const getAdminGroups = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_GROUPS_REQUEST});
    const parentAccountId = getAccountId(getState);
    const {currentAccount} = getState()?.globalGroup;
    const isValid = pageId === 1 && isNull(filter);
    const data: LoadAccountsParams = {
      parentAccountId,
      subAccountIds : includeSubGroups
        ? getAllAccountIds(getState, parentAccountId, true).toString()
        : null,
      pageId,
      pageSize,
      includeSubGroups,
      groupSearchKey : 'parent'
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = addEscapeCharsToFilter(filter);
    }
    try {
      if (isValid) {
        data.pageSize = pageSize - 1;
      }
      let {accounts: items, totalCount} = await loadAccounts(data);
      dispatch({type: GET_ADMIN_GROUPS_RESPONSE});
      const hasCurrentAccount = items?.some(({id}) => id === currentAccount?.id);
      if (isValid && hasCurrentAccountEditAccess() && !hasCurrentAccount) {
        items?.unshift(currentAccount);
        totalCount++;
      }
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_GROUPS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Given a group, assigns the group's parent account applications to the group.
 */
export const setGroupAssignedApplications = async(group: Account): Promise<Account> => {
  const applications = await getApplications(entityId(group.parent), true);
  group.applications = applications as Links;
  return group;
};

/**
 * @addAdminGroup
 *
 * Invoked when the Add Admin Group editor is to be displayed or removed.
 */
export const addAdminGroup = (show = true) => ({
  type : ADD_ADMIN_GROUP, show
});

/**
 * @addGroup
 *
 * Invoked when a new group is to be created; consolidating the process of Group
 * creation into a single transaction. The process for new Group creation is as
 * outlined below.
 *
 * 1.) Retreives the applications assigned to the parent account of the group
 *     that is to be created.
 *
 * 2.) Creates the new Group (with the parent account's applications assigned
 *     to the Group).
 *
 * 3.) Notifies the MicroServices of the account changes.
 *
 * 4.) Dispatches an initialization refresh event, which informs the initialization
 *     state to reload the account hierarchy.
 *
 */
export const addGroup = (group: Account): AsyncActionCreator => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_ADMIN_GROUP_REQUEST});
    group = await setGroupAssignedApplications(group);
    try {
      const created = await createAccount(group);
      await dispatch(refreshInitialization());
      dispatch({type: ADD_ADMIN_GROUP_RESPONSE, group: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.group)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_GROUP_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.group, err)));
      error(err);
    }
  };
};

/**
 * @editAdminGroup
 *
 * Invoked when the Edit Admin Group editor is to be displayed or removed.
 */
export const editAdminGroup = (group: Account, show = true) => ({
  type : EDIT_ADMIN_GROUP, group, show
});

/**
 * @editGroup
 *
 * Invoked when an existing group is to be updated; consolidating the process of
 * Group updates into a single transaction. The process for editing a Group is
 * outlined below.
 *
 * 1.) Update the given group against CTC Services.
 *
 * 2.) Notifies the MicroServices of the account changes.
 *
 * 3.) Dispatches an initialization refresh event, which informs the initialization
 *     state to reload the account hierarchy.
 *
 * 4.) Remove the groups properties from the GroupProperties cache.
 *
 */
export const editGroup = (group: Account, isCurrentGroup = false): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_GROUP_REQUEST});
    try {
      const updated = await updateAccount(group);
      await dispatch(refreshInitialization());
      if (GroupProperties.isCacheExist(updated.id)) {
        await GroupProperties.getInheritedProperties(updated.id, true);
      }
      removeQueries(QueryKey.ACCOUNT_FEATURES, false);
      removeQueries(QueryKey.ACCOUNT_ANCESTOR_FEATURES, false);
      dispatch({type: EDIT_ADMIN_GROUP_RESPONSE, group: updated});
      isCurrentGroup && dispatch(setCurrentAccount(updated));
      dispatch(toggleUserPrompt(successEditOptions(Entity.group)));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_GROUP_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.group, err)));
      error(err);
    }
  };
};

/**
 * @deleteAdminGroup
 *
 * Invoked when the Delete Admin Account Dialog is to be displayed or removed.
 */
export const deleteAdminGroup = (group: Account, show = true) => ({
  type : DELETE_ADMIN_GROUP, group, show
});

/**
 * @deleteGroup
 *
 * Invoked when an existing group is to be deleted; consolidating the process of
 * Group delete into a single transaction. The process for deleting a Group is
 * outlined below.
 *
 * 1.) Delete the given group against CTC Services.
 *
 * 2.) Notifies the MicroServices of the account changes.
 *
 * 3.) Dispatches an initialization refresh event, which informs the initialization
 *     state to reload the account hierarchy.
 *
 */
export const deleteGroup = (group: Account) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_GROUP_REQUEST});
    try {
      const deleted = await deleteAccount(group);
      await dispatch(refreshInitialization());
      GroupProperties.removeFromCache(deleted.id);
      dispatch({type: DELETE_ADMIN_GROUP_RESPONSE, group: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_GROUP_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @auditAdminGroup
 *
 * Invoked when the Audit Admin Group editor is to be displayed or removed.
 */
export const auditAdminGroup = (group: Account, show = true) => ({
  type : AUDIT_ADMIN_GROUP, group, show
});

/**
 * Invoked when the Add Admin Group editor is to be displayed or removed.
 */
export const importAdminGroups = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.group));
    dispatch({type: IMPORT_ADMIN_GROUPS, show, result});
  };
};

/**
 * Invoked whenever any update such as adding, updating, removing, or bulk importing
 * of the group in Admin-Group page.
 */
export const adminGroupGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_GROUPS_GRID_REFRESHED};
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_GROUPS_GRID_FILTER_MODEL, model
});
